import React, { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Star, FileText, MessageSquare, Heart, Users, AlertTriangle, Lightbulb, Target } from 'lucide-react';
import { NoteType } from '@/types/notes';
import { AnalysisNote } from '@/services/analysis';

interface AnalysisStickyNoteData extends AnalysisNote {
  onMoveToCluster?: (noteId: string, clusterId: string) => void;
}

const noteTypeLabels: Record<NoteType, string> = {
  note: 'Note',
  quote: 'Quote',
  emotion: 'Emotion',
  behaviour: 'Behaviour',
  pain: 'Pain Point',
  insight: 'Insight',
  opportunity: 'Opportunity'
};

const noteTypeColors: Record<NoteType, string> = {
  note: 'bg-gray-100 border-gray-300',
  quote: 'bg-yellow-100 border-yellow-300',
  emotion: 'bg-pink-100 border-pink-300',
  behaviour: 'bg-blue-100 border-blue-300',
  pain: 'bg-red-100 border-red-300',
  insight: 'bg-green-100 border-green-300',
  opportunity: 'bg-purple-100 border-purple-300'
};

const noteTypeIcons: Record<NoteType, React.ComponentType<{ className?: string }>> = {
  note: FileText,
  quote: MessageSquare,
  emotion: Heart,
  behaviour: Users,
  pain: AlertTriangle,
  insight: Lightbulb,
  opportunity: Target
};

export const AnalysisStickyNote = memo(({ id, data }: NodeProps) => {
  const noteData = data as unknown as AnalysisStickyNoteData;
  const TypeIcon = noteTypeIcons[noteData.type];
  const colorClasses = noteTypeColors[noteData.type];
  const highlightClasses = noteData.highlighted ? 'ring-2 ring-yellow-400 ring-offset-2' : '';

  return (
    <Card className={`min-w-64 max-w-80 p-4 border-2 shadow-lg ${colorClasses} ${highlightClasses} cursor-move`}>
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 !bg-gray-400 !border-2 !border-white"
      />
      
      {/* Header with type and source */}
      <div className="flex items-center justify-between mb-3">
        <Badge variant="secondary" className="text-xs font-medium flex items-center gap-1">
          <TypeIcon className="w-3 h-3" />
          {noteTypeLabels[noteData.type]}
        </Badge>
        {noteData.highlighted && (
          <Star className="w-4 h-4 text-yellow-500 fill-current" />
        )}
      </div>

      {/* Content */}
      <div className="text-sm text-gray-700 whitespace-pre-wrap break-words mb-3">
        {noteData.content}
      </div>

      {/* Source interview badge */}
      <div className="flex items-center justify-between">
        <Badge variant="outline" className="text-xs truncate max-w-48">
          {noteData.source_interview_name}
        </Badge>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="w-3 h-3 !bg-gray-400 !border-2 !border-white"
      />
    </Card>
  );
});

AnalysisStickyNote.displayName = 'AnalysisStickyNote';